import { useMemo } from 'react';
import { useMachineRegisterBinding } from '../../../../api/hooks/binding/machine-register.binding';
import { useRobotOperationFlowExecutionIndex } from '../../../../api/hooks/binding/robot-operation-monitor.binding';
import {
  ProgramState,
  SafetyState,
} from '../../../../components/machine/machine-state';
import {
  isProgramState,
  isSafetyState,
} from '../../../../components/machine/machine.utils';
import { useScratchpadPlaylistContext } from '../../../../components/machine/playlist/scratchpad-playlist.hooks';
import { useScratchpadServiceContext } from '../../../../components/machine/scratchpad/scratchpad-service-context-provider';
import { getLocalizedText } from '../../../../i18n/i18n.utils';
import { useMachineContext } from '../../../../pages/machine/machine-context-provider';
import { useRobotManufacturerContext } from '../../../../pages/machine/robot-manufacturer-context';
import { hasBeaconEngineSupport } from '../../../../robot/robot.utils';

export interface ExecutionInfo {
  index: number;
  state: ExecutionInfoState;
  message?: string;
}

export enum ExecutionInfoState {
  ERROR = 'error',
  PLAYING = 'playing',
  STOPPED = 'stopped',
  PAUSED = 'paused',
}

const EXECUTION_INDEX_REGISTER = 24;

interface ExecutionInfoResult {
  executedIndex?: number;
  scratchpadId?: number;
}

const useLiveFlowExecution = (): ExecutionInfo | undefined => {
  const { machine } = useMachineContext();
  const { scratchpad } = useScratchpadServiceContext();
  const { statuses } = useScratchpadPlaylistContext();
  const manufacturerContext = useRobotManufacturerContext();
  const isBeaconEngine = hasBeaconEngineSupport(manufacturerContext);

  // Non-BeaconEngine robots: use register binding
  const skipRegister = isBeaconEngine;
  const result: ExecutionInfoResult | undefined = useMachineRegisterBinding(
    machine?.id as string,
    {
      context: {
        registers: [
          {
            type: 'output',
            dataType: 'int',
            index: EXECUTION_INDEX_REGISTER,
            outputKey: 'executedIndex',
          },
        ],
      },
    },
    { skip: skipRegister }
  );

  // BeaconEngine: use robot operation monitor
  const skipMonitor = !isBeaconEngine;
  const monitorExecutionIndex = useRobotOperationFlowExecutionIndex(
    machine?.id as string,
    { skip: skipMonitor }
  );

  return useMemo((): ExecutionInfo | undefined => {
    let isCurrentScratchpad = false;

    // Are we viewing the same scratchpad as the active one in the playlist?
    isCurrentScratchpad = (statuses ?? []).some((status) => {
      return status.isActive && status.scratchpadId === scratchpad.id;
    });

    if (isCurrentScratchpad && machine) {
      const isError = isSafetyState(machine, [
        SafetyState.SAFETY_MODE_PROTECTIVE_STOP,
        SafetyState.SAFETY_MODE_FAULT,
        SafetyState.SAFETY_MODE_ROBOT_EMERGENCY_STOP,
      ]);

      const isPlaying = isProgramState(machine, [
        ProgramState.PROGRAM_PLAYING,
        ProgramState.PROGRAM_PAUSED,
      ]);

      const isPaused = isProgramState(machine, [ProgramState.PROGRAM_PAUSED]);

      let state: ExecutionInfoState;
      let message: string | undefined;
      const index = isBeaconEngine
        ? monitorExecutionIndex ?? -1
        : result?.executedIndex ?? -1;

      if (isError && isPlaying) {
        state = ExecutionInfoState.ERROR;
        message = getLocalizedText('common:protective_stop');
      } else if (isPaused) {
        state = ExecutionInfoState.PAUSED;
        message = getLocalizedText('common:program_paused');
      } else if (isPlaying && !isPaused) {
        state = ExecutionInfoState.PLAYING;
      } else {
        state = ExecutionInfoState.STOPPED;
        message = getLocalizedText('common:program_stopped');
      }

      const info: ExecutionInfo = {
        index,
        state,
        message,
      };

      return info;
    }
  }, [
    machine,
    result,
    monitorExecutionIndex,
    isBeaconEngine,
    statuses,
    scratchpad.id,
  ]);
};

export { useLiveFlowExecution };
